import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Grid,
  Avatar,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/api';
import { formatPortalDate } from '../utils/dateFormat';

const inventoryStorageLocations = ['Shelf', 'Solvent Shed', 'Shop'];

function movementEndpointValue(truckId, location, fallback = '') {
  if (truckId) return `truck:${truckId}`;
  if (location) return `location:${location}`;
  return fallback;
}

function getMovementChipSx(type) {
  const t = String(type || '').toLowerCase();
  if (t === 'removed') return { backgroundColor: '#ffebee', color: '#c62828' };
  if (t === 'assigned') return { backgroundColor: '#e3f2fd', color: '#1565c0' };
  if (t === 'unassigned') return { backgroundColor: '#eeeeee', color: '#424242' };
  if (t === 'installed') return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
  if (t === 'transferred') return { backgroundColor: '#fff3e0', color: '#e65100' };
  return { backgroundColor: '#eeeeee', color: '#424242' };
}

export default function PartMovements() {
  const [movements, setMovements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [parts, setParts] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState({ open: false, partLabel: '' });

  const partById = useMemo(() => {
    const map = new Map();
    (parts || []).forEach((p) => map.set(p.id, p));
    return map;
  }, [parts]);

  const truckNameById = useMemo(() => {
    const map = new Map();
    (trucks || []).forEach((t) =>
      map.set(t.id, t.name || `${t.make || ''} ${t.model || ''}`.trim() || `Truck ${t.id}`)
    );
    return map;
  }, [trucks]);

  const getTruckName = (id, fallback = 'Unassigned') => {
    if (!id) return fallback;
    return truckNameById.get(id) || `Truck #${id}`;
  };

  async function loadReferenceData() {
    const [partsRes, trucksRes] = await Promise.all([api.get('/parts'), api.get('/trucks')]);
    setParts(partsRes.data || []);
    setTrucks(trucksRes.data || []);
  }

  async function loadPage(nextPage = page, nextRowsPerPage = rowsPerPage) {
    try {
      setLoading(true);
      const offset = nextPage * nextRowsPerPage;
      const res = await api.get('/part-movements', {
        params: { limit: nextRowsPerPage, offset, search: searchTerm.trim() || undefined, sort: sortBy },
      });
      const payload = res.data || {};
      const rows = payload.rows || [];
      setMovements(rows);
      setTotal(Number(payload.total || rows.length));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      await loadReferenceData();
    })();

    const handler = async () => {
      await loadReferenceData();
      setPage(0);
      await loadPage(0, rowsPerPage);
    };
    window.addEventListener('dataUpdated', handler);
    return () => window.removeEventListener('dataUpdated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => loadPage(page, rowsPerPage), 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, sortBy]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const openEditor = (movement) => {
    const date = movement.created_at ? new Date(movement.created_at) : new Date();
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setEditing({ ...movement, created_at: localDate });
    setEditError('');
  };

  const updateEditing = (field, value) => setEditing((current) => ({ ...current, [field]: value }));

  const updateMovementEndpoint = (side, value) => {
    const isTruck = String(value).startsWith('truck:');
    const isLocation = String(value).startsWith('location:');
    setEditing((current) => ({
      ...current,
      [`${side}_truck_id`]: isTruck ? Number(String(value).slice(6)) : null,
      [`${side}_location`]: isLocation ? String(value).slice(9) : null,
    }));
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      setEditError('');
      await api.put(`/part-movements/${editing.id}`, {
        ...editing,
        part_id: Number(editing.part_id),
        from_truck_id: editing.from_truck_id || null,
        to_truck_id: editing.to_truck_id || null,
        from_location: editing.from_location || null,
        to_location: editing.to_location || null,
        created_at: new Date(editing.created_at).toISOString(),
      });
      const savedPart = partById.get(Number(editing.part_id));
      setSaveNotice({
        open: true,
        partLabel: savedPart ? `${savedPart.sku} — ${savedPart.part_type}` : `Transfer #${editing.id}`,
      });
      setEditing(null);
      await loadPage(page, rowsPerPage);
    } catch (error) {
      setEditError(error?.response?.data?.error || 'Unable to update this inventory transfer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.75, md: 2 }, minHeight: '100dvh' }}>
      <Card className="portal-page-header"
        sx={{
          mb: 1.25,
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white',
          borderRadius: 2.25,
          boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)',
        }}
      >
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                backgroundColor: '#00ff97',
                boxShadow: '0 3px 14px rgba(0, 255, 151, 0.35)',
              }}
            >
              <HistoryIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: '#001f3f' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05 }}>
                Inventory Transfer
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                Search, review, and correct inventory transfer records
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => loadPage(page, rowsPerPage)}
              disabled={loading}
              sx={{
                backgroundColor: '#00ff97',
                color: '#001f3f',
                fontWeight: 'bold',
                px: 1.55,
                py: 0.62,
                minHeight: 36,
                borderRadius: 1.6,
                fontSize: { xs: '0.78rem', sm: '0.84rem' },
                '&:hover': { backgroundColor: '#00cc7a' },
              }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <TextField
                fullWidth
                placeholder="Search vehicles, Shelf, Solvent Shed, Shop, SKU, or part name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: { xs: '100%', sm: 230 } }}>
                <InputLabel id="inventory-transfer-sort-label">Sort records</InputLabel>
                <Select
                  labelId="inventory-transfer-sort-label"
                  value={sortBy}
                  label="Sort records"
                  onChange={(event) => { setSortBy(event.target.value); setPage(0); }}
                >
                  <MenuItem value="date_desc">Date - Descending</MenuItem>
                  <MenuItem value="date_asc">Date - Ascending</MenuItem>
                  <MenuItem value="alphabetical">Alphabetical</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {movements.length} records on this page
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {total.toLocaleString()} matching transfer records
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Part</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Movement</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>From</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>To</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Notes</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>By</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((m) => {
              const part = partById.get(m.part_id);
              return (
                <TableRow key={m.id} sx={{ '&:hover': { backgroundColor: '#f7faff' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.created_at ? formatPortalDate(m.created_at, { includeTime: true }) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Inventory2Icon sx={{ fontSize: 18, color: '#2e7d32' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#001f3f' }}>
                          {part ? part.sku : `Part #${m.part_id}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {part ? part.part_type : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={(m.movement_type || 'movement').toString().toUpperCase()}
                      sx={{ ...getMovementChipSx(m.movement_type), fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {m.from_location || getTruckName(m.from_truck_id, 'Shelf')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {m.to_location || getTruckName(m.to_truck_id)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.notes || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.moved_by || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit inventory transfer">
                      <IconButton size="small" color="primary" onClick={() => openEditor(m)} aria-label={`Edit transfer ${m.id}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && movements.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No inventory transfer records match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        count={total}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 150]}
        sx={{ mt: 1, borderRadius: 2 }}
      />

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon color="primary" /> Edit Inventory Transfer
        </DialogTitle>
        {editing && (
          <DialogContent dividers>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                pt: 0.5,
                '& .MuiFormControl-root': { minWidth: 0 },
                '& .MuiInputBase-root': { width: '100%' },
              }}
            >
              {editError && <Box sx={{ gridColumn: '1 / -1' }}><Alert severity="error">{editError}</Alert></Box>}
              <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <FormControl fullWidth required>
                  <InputLabel>Part</InputLabel>
                  <Select value={editing.part_id || ''} label="Part" onChange={(e) => updateEditing('part_id', e.target.value)}>
                    {!partById.has(editing.part_id) && editing.part_id && (
                      <MenuItem value={editing.part_id}>Part #{editing.part_id} (archived or unavailable)</MenuItem>
                    )}
                    {parts.map((part) => <MenuItem key={part.id} value={part.id}>{part.sku} — {part.part_type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FormControl fullWidth required>
                  <InputLabel>Transfer type</InputLabel>
                  <Select value={editing.movement_type || ''} label="Transfer type" onChange={(e) => updateEditing('movement_type', e.target.value)}>
                    {['assigned', 'transferred', 'installed', 'removed', 'unassigned'].map((type) => (
                      <MenuItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <TextField fullWidth required type="datetime-local" label="Date" value={editing.created_at} onChange={(e) => updateEditing('created_at', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FormControl fullWidth>
                  <InputLabel>From</InputLabel>
                  <Select value={movementEndpointValue(editing.from_truck_id, editing.from_location, 'location:Shelf')} label="From" onChange={(e) => updateMovementEndpoint('from', e.target.value)}>
                    {inventoryStorageLocations.map((location) => <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>)}
                    {trucks.map((truck) => <MenuItem key={truck.id} value={`truck:${truck.id}`}>{getTruckName(truck.id)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FormControl fullWidth>
                  <InputLabel>To</InputLabel>
                  <Select value={movementEndpointValue(editing.to_truck_id, editing.to_location)} label="To" onChange={(e) => updateMovementEndpoint('to', e.target.value)}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {inventoryStorageLocations.map((location) => <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>)}
                    {trucks.map((truck) => <MenuItem key={truck.id} value={`truck:${truck.id}`}>{getTruckName(truck.id)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <TextField fullWidth label="Moved by" value={editing.moved_by || ''} onChange={(e) => updateEditing('moved_by', e.target.value)} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <TextField fullWidth multiline minRows={3} label="Notes" value={editing.notes || ''} onChange={(e) => updateEditing('notes', e.target.value)} />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Alert severity="info">Editing corrects this history record only. Current inventory quantities are not recalculated.</Alert>
              </Box>
            </Box>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving || !editing?.part_id || !editing?.movement_type || !editing?.created_at}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveNotice.open}
        autoHideDuration={4500}
        onClose={(_event, reason) => reason !== 'clickaway' && setSaveNotice((notice) => ({ ...notice, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: { xs: 1, sm: 7 }, mr: { xs: 0, sm: 1 } }}
      >
        <Paper
          elevation={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: { xs: 'calc(100vw - 32px)', sm: 410 },
            maxWidth: '100%',
            px: 2,
            py: 1.6,
            color: 'white',
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #087f5b 0%, #00a86b 100%)',
            border: '1px solid rgba(255,255,255,0.24)',
            boxShadow: '0 14px 38px rgba(8,127,91,0.34)',
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 27 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '0.96rem', lineHeight: 1.25 }}>
              Inventory transfer saved
            </Typography>
            <Typography sx={{ mt: 0.25, opacity: 0.9, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {saveNotice.partLabel} was updated successfully.
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Dismiss save notification"
            onClick={() => setSaveNotice((notice) => ({ ...notice, open: false }))}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Snackbar>
    </Box>
  );
}
